import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE_ITEM } from '../../../../../../../webcomponents.constants';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GzEnumFilterWebComponent } from '../../../../../../../ui/gzEnumFilter';
import { getSectionLabelById } from '../../../../searchableComponents/searchablePolicyPanel/utils/treeItems';
import {
  getAvailableModulesForInheritance,
  getAvailableSectionsForInheritanceByModule,
} from '../../../../utils/inheritance';
import { GzSingleSelectWebComponent } from '../../../../../../../ui/gzSingleSelectDropdown';
import { InputWidthValues } from '../../../../../../../ui/gzInput';
import { LabelWidthValues } from '../../../../../../../types/common/types';
import {
  PolicyDependenciesContainer,
  PolicyTemplatesApiClients,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { InheritancePolicyData, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { OnNewGridItemAdd } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/events/events';
import _ from 'lodash';
import { selectPoliciesState } from '../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { getPhantom } from '../../../../redux/actions';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE_ITEM)
export class GzGeneralInheritanceRulesItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#moduleId')
  moduleId: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#sectionId')
  sectionId: GzFormFieldWebComponent<GzEnumFilterWebComponent>;

  @query('#policyId')
  policyId: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  private policyName: GzHardCodedFormField<string> = new GzHardCodedFormField('');

  private clients: PolicyTemplatesApiClients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'PolicyTemplates'
  ) as PolicyTemplatesApiClients;
  private inheritablePolicies: InheritancePolicyData[] = [];

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field">
        <gz-form-field id="moduleId">
          <gz-single-select
            @selectionChange="${this.handleModuleFieldChange}"
            gz-form-input
            label="${translateFn('Module')}"
            .filterOptions=${getAvailableModulesForInheritance()}
            @open="${this.resetErrorState}"
          ></gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="sectionId" disabled>
          <gz-enum-filter
            labelWidth=${InputWidthValues.XLARGE}
            gz-form-input
            canSelectAll
            allitemslabel="${translateFn('All')}"
            label="${translateFn('This module has no sections')}"
            confirmActionButtonString="${translateFn('Apply')}"
            .filterOptions=${[]}
            @invalidOpen="${this.resetErrorState}"
          ></gz-enum-filter>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="policyId">
          <gz-single-select
            gz-form-input
            hasDynamicSearch
            labelWidth="${LabelWidthValues.XLARGE}"
            label="${translateFn('Policy')}"
            placeholder="${translateFn('No policy selected')}"
            searchPlaceholderString="${translateFn('Search')}"
            noSearchResultsString="${translateFn('No policy available.')}"
            @open="${this.onPolicyDropdownOpen}"
            @selectionChange="${this.onPolicySelectionChanged}"
            @searchInputChange="${this.onPolicySearchInputChange}"
          ></gz-single-select>
        </gz-form-field>
      </div>
    `;
  }

  private handleModuleFieldChange(event: CustomEvent) {
    const moduleId = event.detail.selectedValue;
    const { canSelectAll, filterOptions } = getAvailableSectionsForInheritanceByModule(moduleId);

    this.sectionId.Input.canSelectAll = canSelectAll;
    this.sectionId.Input.allItemsLabel = canSelectAll
      ? getSectionLabelById(SectionId.ALL)
      : filterOptions.map(item => item.label).join(', ');
    this.sectionId.Input.filterOptions = filterOptions;
    this.sectionId.disabled = !filterOptions.length;
    this.sectionId.Input.label = !filterOptions.length
      ? translateFn('This module has no sections')
      : translateFn('Section');
    this.sectionId.validate();
  }

  private onPolicyDropdownOpen() {
    this.resetErrorState();
    this.getInheritablePolicies('');
  }

  private onPolicySearchInputChange(event: CustomEvent) {
    const searchString = event.detail.value;
    this.getInheritablePolicies(searchString);
  }

  private onPolicySelectionChanged(event: CustomEvent) {
    // get data
    const data = this.inheritablePolicies.find(item => item._id === event.detail.selectedValue);
    this.policyName.value = data?.name ?? '';
  }

  private async getInheritablePolicies(searchString: string) {
    this.policyId.Input.isLoading = true;
    const state = selectPoliciesState(store.getState());
    const policyId = getPhantom(state) ? null : PolicyPanelManager.getInstance().getPolicyId();

    try {
      const result = await this.clients.getInheritablePolicies(policyId, 100, searchString, 1);
      this.inheritablePolicies = result.data;

      this.policyId.Input.filterOptions = this.inheritablePolicies.map(item => {
        return {
          label: item.name,
          value: item._id,
        };
      });
    } catch (error) {
      this.inheritablePolicies = [];
    } finally {
      this.policyId.Input.isLoading = false;
    }
  }

  focusFirstInput(): void {
    this.moduleId.Input.classList.add('focus');
  }

  public setError(error: string) {
    this.moduleId.setError(error);
    this.sectionId.setError(error);
    this.sectionId.Input.isError = true;
    this.sectionId.Input.errorMessage = error;

    this.policyId.setError(error);
  }

  public resetErrorState() {
    this.moduleId.Input.classList.remove('focus');
    this.moduleId.validate();
    this.sectionId.Input.isError = false;
    this.sectionId.validate();
    this.policyId.validate();
  }

  protected addNewItem() {
    const value = this.formGroup.value;
    let sectionIds = _.get(value, 'sectionIds', [null]) as (SectionId | null)[];

    if (this.sectionId.Input.canSelectAll && sectionIds.length === this.sectionId.Input.filterOptions.length) {
      sectionIds = [SectionId.ALL];
    }

    const policyInheritance = this.inheritablePolicies.find(item => item._id === value.policyId)?.inheritance || [];
    const hasCollision = policyInheritance.some(inheritance => {
      return (
        inheritance.moduleId === value.moduleId &&
        (sectionIds.includes(inheritance.sectionId) ||
          inheritance.sectionId === SectionId.ALL ||
          sectionIds.includes(SectionId.ALL))
      );
    });

    if (hasCollision) {
      this.setError(
        translateFn(
          'Unable to add the rule. At least one of these sections or modules is inherited by the selected policy from another policy. Select the original policy to create a rule.'
        )
      );
      return;
    }

    this.dispatchEvent(
      new OnNewGridItemAdd({
        policyName: value.policyName,
        policyId: value.policyId,
        sectionIds: sectionIds,
        moduleId: value.moduleId,
      })
    );
  }

  createFormGroup(): void {
    this.moduleId.Validators = [RequiredValidator(translateFn('Required field'))];
    this.sectionId.Validators = [RequiredValidator(translateFn('Required field'))];
    this.policyId.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = new FormGroup({
      moduleId: this.moduleId,
      sectionIds: this.sectionId,
      policyId: this.policyId,
      policyName: this.policyName,
    });
  }

  resetForm(): void {
    this.moduleId.value = '';
    this.moduleId.resetField();

    this.sectionId.value = [];
    this.sectionId.resetField();

    this.policyId.value = '';
    this.policyId.resetField();
    this.policyId.validate();

    this.policyName.value = '';
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm();
  }
}
